/**
 * API Service for communicating with backend
 * Handles all HTTP requests to the FastAPI backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Configure API headers
 */
const getHeaders = () => ({
  'Content-Type': 'application/json',
});

/**
 * Error handler for API calls
 */
const handleApiError = (error) => {
  console.error('API Error:', error);
  if (error.response) {
    // Backend responded with error
    throw {
      status: error.response.status,
      message: error.response.data?.detail || 'An error occurred',
      data: error.response.data,
    };
  } else if (error.request) {
    // Request made but no response
    throw {
      status: 0,
      message: 'No response from server. Check backend URL configuration.',
      data: error.request,
    };
  } else {
    // Something else happened
    throw {
      status: 0,
      message: error.message || 'Unknown error occurred',
      data: error,
    };
  }
};

/**
 * Health check - verify backend is running
 */
export const checkBackendHealth = async () => {
  try {
    const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';
    const response = await fetch(`${backendUrl}/health`, {
      method: 'GET',
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Health check failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Backend health check failed:', error);
    return { status: 'unhealthy', error: error.message };
  }
};

/**
 * Get root endpoint - verify API is accessible
 */
export const getApiRoot = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`API root failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get all status checks from backend
 */
export const getStatusChecks = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/status`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch status checks: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Create a new status check
 */
export const createStatusCheck = async (clientName) => {
  try {
    const response = await fetch(`${API_BASE_URL}/status`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ client_name: clientName }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create status check: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Generic API call function
 * @param {string} endpoint - The endpoint path (e.g., '/status')
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {object} data - Data to send in request body
 */
export const apiCall = async (endpoint, method = 'GET', data = null) => {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      method,
      headers: getHeaders(),
    };

    if (data && (method === 'POST' || method === 'PUT')) {
      config.body = JSON.stringify(data);
    }

    const response = await fetch(url, config);

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error);
  }
};

export default {
  checkBackendHealth,
  getApiRoot,
  getStatusChecks,
  createStatusCheck,
  apiCall,
};
